<template>
  <div class="plan">
    <div class="plan__title">
      <div class="plan__title-text">
        <h3 class="plan__title-name">Your Plan</h3>
        <h2 class="plan__title-content">{{ billing.planName }}</h2>
      </div>
      <div>
        <v-wait v-if="billing.allowPlanChange" for="Subscribing">
          <template v-slot:waiting>
            <span class="plan__title-btn btns btns btns--font-large">
              Processing...
              <button-spinner />
            </span>
          </template>
          <button
            v-if="
              billing.cancel_at_period_end || billing.change_plan_at_period_end
            "
            class="plan__title-btn btns btns btns--font-large"
            @click="reactivate"
          >
            <span> Reactivate </span>
          </button>
          <button
            v-else
            class="plan__title-btn btns btns--white btns--font-large"
            @click="switchMonthly"
          >
            <span> Switch to Monthly </span>
          </button>
        </v-wait>
        <div v-if="error !== null && error !== ''" class="subscription-error">
          {{ error }}
        </div>
        <ChangeBillingPeriodPopup
          ref="popupMonthly"
          :billing="billing"
          :targetPlan="billing.monthlyPlan"
          title="Change to Monthly Subscription"
          confirmButtonText="Change to Monthly"
        >
          <p>
            By changing to monthly billing, you'll lose out on
            {{ billing.switchToAnnualSavings.percent }}% savings.
          </p>
          <p>
            You won't be charged until your annual subscription credits are used
            up.
          </p>
        </ChangeBillingPeriodPopup>
      </div>
    </div>

    <div class="plan-details-annual">
      <h3 class="plan-details-title plan-details-title--annual">Details</h3>
      <div class="plan-details-annual__top">
        <div class="plan-details-annual__top-left">
          <p class="plan-details-name">{{ billing.plan.nickname }} Plan</p>
        </div>
        <div class="plan-details-annual__top-right">
          <p
            v-if="billing.showPricesAfterDiscount"
            class="plan-details-annual__top-right-text plan-details__top-right-text--discount"
          >
            $147.75/yr
            <span> ($0 for 3mo, then $16.42/mo) </span>
          </p>
          <p v-else class="plan-details-annual__top-right-text">
            {{ billing.currentPeriodPrice.withPeriod.perYear }}
          </p>
        </div>
      </div>
    </div>

    <PlanDate :billing="billing" />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { BillingView } from '@/models/billing'
import ChangeBillingPeriodPopup from './ChangeBillingPeriodPopup.vue'

import PlanDate from './BillingDataPaid.PlanDate.vue'
import { PlanChangeFailedError } from '@/services/cordova.purchase.types'

const props = defineProps<{
  billing: BillingView
}>()

const wait = useWait()
const emit = defineEmits(['subscribed'])

const popupMonthly = ref<InstanceType<typeof ChangeBillingPeriodPopup> | null>(
  null,
)

const error = ref<string | null>(null)

async function switchMonthly(): Promise<void> {
  if (!props.billing.is_trial) {
    popupMonthly.value?.show()
    return
  }

  error.value = null
  try {
    wait.start('Subscribing')
    await props.billing.subscribeTo(props.billing.monthlyPlan)
    emit('subscribed')
    wait.end('Subscribing')
  } catch (err) {
    if (err instanceof PlanChangeFailedError) {
      error.value = err.message
    }
    wait.end('Subscribing')
  }
}

async function reactivate(): Promise<void> {
  try {
    wait.start('Subscribing')
    await props.billing.subscribeTo(props.billing.plan)
    emit('subscribed')
    wait.end('Subscribing')
  } catch (error) {
    wait.end('Subscribing')
  }
}
</script>

<style lang="scss" scoped>
.subscription-error {
  padding-top: 10px;
  font-size: 16px;
  text-align: center;
  color: $c-red;
}
</style>
